import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import {usePageTitle} from "../helpers/page-title";
import Social from "../includes/social";

const ARTICLES_PER_PAGE = 20;

export default function NewsPage() {
    const { t, i18n, ready } = useTranslation();

    usePageTitle(`${t('title.news')} | ${t('title.gaocna')}`)

    const [newsData, setNewsData] = useState([]);
    const [loading, setLoading] = useState(true);

    // Initialize currentPage from URL or default to 1
    const [currentPage, setCurrentPage] = useState(() => {
        if (typeof window !== "undefined") {
            const params = new URLSearchParams(window.location.search);
            const page = parseInt(params.get("currentpage"));
            return page && page > 0 ? page : 1;
        }
        return 1;
    });

    // Fetch news after i18n is ready and language is set
    useEffect(() => {
        if (!ready) return;

        const params = new URLSearchParams(window.location.search);
        const urlLang = params.get("lang");

        // Function to fetch news
        const fetchNews = async (lang) => {
            setLoading(true);
            try {
                const res = await fetch(`/api/${lang}/news`);
                const data = await res.json();
                const validatedData = Array.isArray(data) ? data : [];
                setNewsData(validatedData);

                // Adjust currentPage if it exceeds total pages
                const totalPages = Math.ceil(validatedData.length / ARTICLES_PER_PAGE);
                setCurrentPage((prev) => (prev > totalPages ? totalPages || 1 : prev));
            } catch (err) {
                console.error("Error fetching news:", err);
                setNewsData([]);
            } finally {
                setLoading(false);
            }
        };

        if (urlLang && urlLang !== i18n.language) {
            // Change language first, then fetch
            i18n.changeLanguage(urlLang).then(() => fetchNews(urlLang));
        } else {
            fetchNews(i18n.language);
        }
    }, [ready, i18n]);

    // Sync currentPage to URL
    useEffect(() => {
        if (typeof window === "undefined") return;

        const url = new URL(window.location.href);
        const urlPage = parseInt(url.searchParams.get("currentpage"));

        if (urlPage !== currentPage) {
            url.searchParams.set("currentpage", currentPage);
            window.history.replaceState({ page: currentPage }, "", url);
        }
    }, [currentPage]);

    // Pagination
    const totalPages = Math.max(1, Math.ceil(newsData.length / ARTICLES_PER_PAGE));
    const paginatedNews = newsData.slice(
        (currentPage - 1) * ARTICLES_PER_PAGE,
        currentPage * ARTICLES_PER_PAGE
    );

    const handleNext = () => {
        if (currentPage < totalPages) setCurrentPage((prev) => prev + 1);
    };

    const handlePrev = () => {
        if (currentPage > 1) setCurrentPage((prev) => prev - 1);
    };

    const handleDotClick = (page) => setCurrentPage(page);

    return (
        <>
        <Social />
        <main className="container" role="news-page">
            <h1 className="page-title">{t("news.title")}</h1>

            {loading ? (
                <div className="loading">{t("news.loading")}</div>
            ) : newsData.length === 0 ? (
                <div className="no-news">{t("news.no-news")}</div>
            ) : (
                <>
                    <div className="news-list" id="news-list">
                        {paginatedNews.map((article) => (
                            <div key={article.id} className="news-card">
                                <div className="img-frame">
                                    <img src={article.thumbnail} alt="News" className="news-image" />
                                </div>
                                <div className="card-content">
                                    <div className="info">
                                        <h2>{article.title}</h2>
                                        <p>
                                            <span lang="en">{article.published_date}</span>
                                        </p>
                                    </div>
                                    <div className="read-more-container">
                                        <a
                                            href={`/news/${article.id}`}
                                            className="read-more-link"
                                        >
                                            {t("news.read-more")}
                                        </a>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>

                    <div className="pagination-control">
                        <button
                            className={`pagination-btn ${currentPage === 1 ? "disabled" : ""}`}
                            onClick={handlePrev}
                            disabled={currentPage === 1}
                        >
                            <i className="bi bi-caret-left-fill"></i>
                        </button>

                        <div className="pagination-indicator">
                            {Array.from({ length: totalPages }, (_, i) => (
                                <div
                                    key={i + 1}
                                    className={`pagination-indicator-dot ${
                                        currentPage === i + 1 ? "active" : ""
                                    }`}
                                    onClick={() => handleDotClick(i + 1)}
                                ></div>
                            ))}
                        </div>

                        <button
                            className={`pagination-btn ${
                                currentPage === totalPages ? "disabled" : ""
                            }`}
                            onClick={handleNext}
                            disabled={currentPage === totalPages}
                        >
                            <i className="bi bi-caret-right-fill"></i>
                        </button>
                    </div>
                </>
            )}
        </main>
        </>
    );
}
